import { useMemo } from "react";
import { useMyStore } from "../../../core/auth/useLoggedInUser";
import { useNotificationsByStore } from "../hooks/useNotificationsByStore";
import { MediaListItemMobile } from "../Molecules/MediaListItemMobile";
import { Media, MediaResponse, RoomMedia } from "../types/media";
import { EmptyMediaPlaceholder } from "../Molecules/EmptyMediaPlaceholder";
import { MediaTypes } from "../constants";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { MEDIA_TASK_TYPE_NAMES } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { useMediaTaskCountsV2 } from "@coworker/apprestructured/src/media-maintenance/hooks/useMediaTaskCountsV2";

interface HomeProps {
  rooms: RoomMedia[];
}

export function Home({ rooms }: HomeProps) {
  const myStore = useMyStore();
  const { data: notifications } = useNotificationsByStore(
    myStore.storeId,
    myStore.countryId
  );

  // Fetch v2 task counts for all medias when flag enabled
  const enableMediaV2Tasks = useFlag(FLAGS.ENABLE_MEDIA_V2_TASKS);
  const { data: taskTypes = [] } = useAllTaskTypes();

  const mediaTaskTypeIds = useMemo(
    () =>
      taskTypes
        .filter((tt) =>
          (MEDIA_TASK_TYPE_NAMES as readonly string[]).includes(tt.name)
        )
        .map((tt) => tt.id),
    [taskTypes]
  );

  const { data: v2TaskCountsMap = new Map() } = useMediaTaskCountsV2(
    enableMediaV2Tasks ? mediaTaskTypeIds : []
  );

  // Combine v1 and v2 task counts per media
  const taskCountsByMediaId = useMemo(() => {
    const map = new Map<string, number>();

    // Count v1 tasks from task links
    (notifications?.taskLinks ?? []).forEach((tl) => {
      map.set(tl.mediaId, (map.get(tl.mediaId) || 0) + 1);
    });

    // Add v2 task counts
    v2TaskCountsMap.forEach((v2Count, mediaId) => {
      map.set(mediaId, (map.get(mediaId) || 0) + v2Count);
    });

    return map;
  }, [notifications?.taskLinks, v2TaskCountsMap]);

  if (rooms.length === 0) {
    return <EmptyMediaPlaceholder mediaType={MediaTypes.HOME} />;
  }
  return (
    <>
      {rooms.map((room: RoomMedia) => (
        <MediaListItemMobile
          key={room.id}
          mediaResponse={
            { media: room, children: [], parent: {} as Media } as MediaResponse
          }
          taskCount={taskCountsByMediaId.get(room.id) || 0}
          alerts={notifications?.alerts ?? []}
        />
      ))}
    </>
  );
}
